import { Box } from "@rebass/grid";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import logger from "@rentiohq/shared-frontend/dist/logger";
import * as contactApi from "@rentiohq/shared-frontend/dist/redux/contact/contact.api";
import {
  EKybStatus,
  EMangoScaStatus,
  EMissingKYCStatus,
  IBrokerKybInfo,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  ESpacings,
  Loading,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { StringParam } from "serialize-query-params";
import {
  getKybReturnUrl,
  handleKybSession,
} from "../../../forms/kycVerification/schema.kycVerification.actions";
import {
  fetchKybInfoAfterReturn,
  getKybOwnerGroups,
  isKybSubmitted,
} from "../../../utils/kyb.utils";
import CollectionFollowUpKycModal from "../../Properties/scenes/Payments/components/CollectionFollowUpV2/CollectionFollowUpKycModal";
import { CollectionFollowUpScaModal } from "../../Properties/scenes/Payments/components/CollectionFollowUpV2/CollectionFollowUpScaModal";

// DEV-2980 (release/2026/Q3): Mangopay asked to renew the verification; the current one stays valid until the deadline
const KYC_RENEWAL_PENDING_STATUS = "renewalPending";

// Once Mangopay has the office's KYB (submitted or approved), SCA is the next step, on the office's bank account
const needsSca = (kybInfo?: IBrokerKybInfo | null) =>
  !!kybInfo?.scaContactId &&
  !!kybInfo.scaBankAccountId &&
  kybInfo.scaStatus !== EMangoScaStatus.Enrolled &&
  (kybInfo.kycStatus === EMissingKYCStatus.Approved || isKybSubmitted(kybInfo));

interface IProps {
  // The office's account holder: Mangopay verifies the office as a company with this person as its representative
  contactId: number;
}

// DEV-2801: an office is a legal Mangopay user, so its account holder is verified through a KYB, not ID cards
export const AccountHolderKyb = ({ contactId }: IProps) => {
  const [kybInfo, setKybInfo] = React.useState<IBrokerKybInfo | null>();
  const [showKybModal, setShowKybModal] = React.useState(false);
  const [showScaModal, setShowScaModal] = React.useState(false);
  const [isResuming, setIsResuming] = React.useState(false);

  const fetchKybInfo = React.useCallback(async () => {
    try {
      const { data } = await contactApi.getKycIdv(contactId);
      setKybInfo(data);

      return data;
    } catch (unknownError) {
      logger.logError({ error: unknownError as Error });
      setKybInfo(null);

      return null;
    }
  }, [contactId]);

  // Back from Mangopay's hosted page (the return URL carries this contact): the submitted session can still read
  // PENDING for a few seconds
  const [queryParamValue, setQueryParamValue] = useQueryParams({
    kybCallbackContactId: StringParam,
  });
  const isKybReturn =
    Number(queryParamValue.kybCallbackContactId) === contactId;

  // The return parameter is cleared once handled, so a reload or a bookmarked URL does not wait on Mangopay again
  const handledKybReturn = React.useRef(false);

  React.useEffect(() => {
    if (isKybReturn && !handledKybReturn.current) {
      handledKybReturn.current = true;
      setQueryParamValue({ kybCallbackContactId: undefined });
      fetchKybInfoAfterReturn(fetchKybInfo).then(returnedKybInfo => {
        if (needsSca(returnedKybInfo)) {
          setShowScaModal(true);
        }
      });

      return;
    }

    // Clearing the parameter re-runs this effect: the return read above already covers it
    if (!handledKybReturn.current) {
      fetchKybInfo();
    }
  }, [fetchKybInfo, isKybReturn, setQueryParamValue]);

  const handleResume = async () => {
    setIsResuming(true);

    try {
      const { data } = await contactApi.startKycIdv(contactId, {
        returnUrl: getKybReturnUrl(contactId),
      });
      handleKybSession(data);

      // No link means Mangopay has the session already (submitted meanwhile): show that instead of "continue"
      if (!data.hostedUrl) {
        fetchKybInfo();
      }
    } catch (unknownError) {
      logger.logError({ error: unknownError as Error });
      showAlert({ type: "error", message: getLocalizedText("general.error") });
    } finally {
      setIsResuming(false);
    }
  };

  const handleModalClose = () => {
    setShowKybModal(false);
    fetchKybInfo();
  };

  if (kybInfo === undefined) {
    return <Loading />;
  }

  if (kybInfo === null) {
    return null;
  }

  const isApproved = kybInfo.kycStatus === EMissingKYCStatus.Approved;
  const isRenewalPending = kybInfo.kycStatus === KYC_RENEWAL_PENDING_STATUS;
  const isFailed = kybInfo.kycStatus === EMissingKYCStatus.Failed;
  const isSubmitted = isKybSubmitted(kybInfo);
  const isOpen =
    kybInfo.kycStatus === EMissingKYCStatus.Requested &&
    kybInfo.kybStatus === EKybStatus.Pending;
  const { refused: refusedOwners, pending: pendingOwners } = getKybOwnerGroups(
    kybInfo.kybOwners,
  );

  const renderStatus = () => {
    if (isRenewalPending) {
      return getLocalizedText("company.account_holder.kyb.renewal_pending");
    }

    if (isApproved) {
      return getLocalizedText("company.account_holder.kyb.approved");
    }

    if (isFailed && kybInfo.failedReason) {
      // A reason without a translation (getLocalizedText answers the key itself) gets the general one
      const reasonKey = `payments.kyc_failed.reason.${kybInfo.failedReason.toLowerCase()}`;
      const reason = getLocalizedText(reasonKey);

      return reason === reasonKey
        ? getLocalizedText("payments.kyc_failed.reason.specific_case")
        : reason;
    }

    if (isOpen) {
      return getLocalizedText(
        "payments.collection.follow_up.kyb_reason.pending",
      );
    }

    if (isSubmitted && refusedOwners.length > 0) {
      return getLocalizedText(
        "payments.collection.follow_up.kyb_reason.owners_refused",
        { names: refusedOwners.map(owner => owner.name).join(", ") },
      );
    }

    if (isSubmitted && pendingOwners.length > 0) {
      return getLocalizedText(
        "payments.collection.follow_up.kyb_reason.owners",
        { names: pendingOwners.map(owner => owner.name).join(", ") },
      );
    }

    if (isSubmitted) {
      return getLocalizedText(
        "payments.collection.follow_up.kyb_reason.review",
      );
    }

    return getLocalizedText("company.account_holder.kyb.not_started");
  };

  const getVariation = () => {
    if (isRenewalPending) {
      return "warning";
    }

    if (isApproved) {
      return "success";
    }

    return isFailed ? "error" : "info";
  };

  const renderAction = () => {
    // A renewal is a new KYB session (kyc-idv opens one for a renewal although the last one is validated)
    if (isRenewalPending) {
      return (
        <Button appearance="primary" onClick={() => setShowKybModal(true)}>
          {getLocalizedText("company.account_holder.kyb.cta.start")}
        </Button>
      );
    }

    if (needsSca(kybInfo)) {
      return (
        <Button appearance="primary" onClick={() => setShowScaModal(true)}>
          {getLocalizedText("form.sca_receiver_as_broker.cta.start")}
        </Button>
      );
    }

    if (isApproved || (isSubmitted && !isFailed)) {
      return null;
    }

    if (isOpen) {
      return (
        <Button
          appearance="primary"
          onClick={handleResume}
          isSubmitting={isResuming}
        >
          {getLocalizedText("form.kyb_receiver_as_broker.cta.resume")}
        </Button>
      );
    }

    return (
      <Button appearance="primary" onClick={() => setShowKybModal(true)}>
        {getLocalizedText("company.account_holder.kyb.cta.start")}
      </Button>
    );
  };

  return (
    <Box mt={ESpacings.loose}>
      <Banner
        hasDismiss={false}
        icon="billStack"
        variation={getVariation()}
        title={getLocalizedText("company.account_holder.kyb.title", {
          companyName: kybInfo.companyName || "",
        })}
      >
        {renderStatus()}
      </Banner>

      {renderAction()}

      {showScaModal && kybInfo.scaContactId && kybInfo.scaBankAccountId && (
        <CollectionFollowUpScaModal
          contactId={kybInfo.scaContactId}
          bankAccountId={kybInfo.scaBankAccountId}
          dismissable={true}
          onSuccess={() => {
            setShowScaModal(false);
            fetchKybInfo();
          }}
          onClose={() => setShowScaModal(false)}
        />
      )}

      {showKybModal && (
        <CollectionFollowUpKycModal
          contactId={contactId}
          asBroker={true}
          onSuccess={handleModalClose}
          onError={() => fetchKybInfo()}
          onClose={handleModalClose}
        />
      )}
    </Box>
  );
};
